import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Button,
  Spinner,
  Flex,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import useDebounce from "../../hooks/useDebounce";
import { CiSearch } from "react-icons/ci";
import { IoCheckmarkOutline } from "react-icons/io5";
import { normalize } from "../../utils/utils";
import { LimitDataSelect } from "../../utils/globalVariable";

const SearchableSelectMulti = ({
  label,
  options,
  initialSelectedOption = [],
  initialSelectedOptionParam = [],
  isShowSearch,
  setValue,
  width,
  minW,
  placeholder,
  state,
  bdTop,
  isRequired,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(
    Array.isArray(initialSelectedOption) && initialSelectedOption.length
      ? initialSelectedOption
      : Array.isArray(initialSelectedOptionParam)
      ? initialSelectedOptionParam
      : []
  );
  // console.log(initialSelectedOption)
  useEffect(() => {
    if (
      Array.isArray(initialSelectedOption) &&
      Array.isArray(initialSelectedOptionParam)
    ) {
      setSelectedOptions(initialSelectedOption);
    }
  }, [JSON.stringify(initialSelectedOption)]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (debouncedSearchTerm) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [debouncedSearchTerm]);

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      const name = normalize(option?.value || ""); // e.g., fullName
      const code = normalize(option?.code || ""); // e.g., mã nhân viên
      const search = normalize(debouncedSearchTerm);
      return name.includes(search) || code.includes(search);
    });
  }, [debouncedSearchTerm, options]);

  const handleSelect = (option) => {
    const exists = selectedOptions?.some((o) => o.key === option.key);
    let updated;

    if (exists) {
      updated = selectedOptions.filter((o) => o.key !== option.key); // Bỏ chọn
    } else {
      updated = [...selectedOptions, option]; // Thêm chọn
    }

    setSelectedOptions(updated);
    setValue && setValue(updated.map((o) => o.key)); // Cập nhật danh sách key ra ngoài
    setSearchTerm("");
  };

  // Xoá toàn bộ selection — stopPropagation để không toggle dropdown
  const hasSelection = selectedOptions.length > 0;

  const handleClearAll = useCallback(
    (e) => {
      e.stopPropagation();
      setSelectedOptions([]);
      setValue?.([]);
      setSearchTerm("");
    },
    [setValue],
  );

  return (
    <Box
      w={width || "auto"}
      maxW={width || "250px"}
      minW={minW || "auto"}
      borderRadius={"12px"}
      minHeight={"44px"}
    >
      {label && (
        <Flex alignItems="center" gap="4px">
          <Text
            fontSize={{ base: "12px" }}
            fontWeight={400}
            lineHeight={"15px"}
            mb={bdTop ? "0" : "6px"}
            color={
              state
                ? "var(--color-option-employee-hover)"
                : bdTop
                ? "var(--color-black-label)"
                : "var(--color-label-employee)"
            }
          >
            {label}
          </Text>
          {isRequired && (
            <Text fontSize={{ base: "12px" }} color={"red"} lineHeight={"15px"}>
              *
            </Text>
          )}
        </Flex>
      )}
      <Menu matchWidth closeOnSelect={false}>
        <MenuButton
          _active={{ border: "1px solid var(--color-option-employee-hover)" }}
          _hover={{ boxShadow: "none" }}
          h={"44px"}
          as={Button}
          rightIcon={
            hasSelection ? (
              <Box
                as="span"
                onClick={handleClearAll}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="18px"
                h="18px"
                borderRadius="full"
                bg="var(--bg-line-employee)"
                cursor="pointer"
                flexShrink={0}
                _hover={{ opacity: 0.7 }}
              >
                <CloseIcon fontSize="7px" color="#666" />
              </Box>
            ) : (
              <ChevronDownIcon color={"#000"} />
            )
          }
          w="100%"
          backgroundColor="#FFF"
          border={"1px solid var(--Line)"}
        >
          <Text
            textAlign={"start"}
            color={
              selectedOptions.length ? "#000" : "var(--Text-color-Disable)"
            }
            fontSize={"14px"}
            fontWeight={500}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {selectedOptions.length
              ? selectedOptions.map((o) => o.value).join(", ")
              : placeholder || "Chọn..."}
          </Text>
        </MenuButton>

        <MenuList borderRadius="12px" maxH="250px" overflow="auto" minW="275px">
          {isShowSearch && (
            <Flex
              alignItems="center"
              gap="12px"
              pl="16px"
              pr="16px"
              borderBottom="0.5px solid var(--bg-line-employee)"
            >
              <CiSearch color="var(--Text-color-Disable)" fontSize="20px" />
              <Input
                p="0px"
                fontSize="14px"
                fontWeight={500}
                color="black"
                _placeholder={{ color: "var(--Text-color-Disable)" }}
                border="none"
                _focus={{ boxShadow: "none" }}
                placeholder="Tìm kiếm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {loading && (
                <Flex justify="center" mt={2}>
                  <Spinner size="sm" />
                </Flex>
              )}
            </Flex>
          )}

          {!loading &&
            filteredOptions.map((option, index) => {
              const isSelected = selectedOptions?.some(
                (o) => o.key === option.key
              );
              return (
                index <= LimitDataSelect && (
                  <Flex key={index}>
                    <MenuItem
                      pl="16px"
                      onClick={() => handleSelect(option)}
                      backgroundColor={isSelected ? "#F9FAFD" : "#FFF"}
                      color={isSelected ? "#1A9ED0" : "#000"}
                    >
                      <Flex alignItems="center" gap="8px">
                        <Text minW="max-content">{option?.value}</Text>
                      </Flex>
                      <IoCheckmarkOutline
                        style={{
                          marginLeft: "auto",
                          visibility: isSelected ? "visible" : "hidden",
                        }}
                      />
                    </MenuItem>
                  </Flex>
                )
              );
            })}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default SearchableSelectMulti;
